import { createElement, Fragment, type ReactNode } from 'react';

import { is } from './helpers';
import { removeTrailingSlash } from './url';

export type FormatTemplateValue = string | number | null | undefined;

/**
 * Format a string with mustache placeholders.
 *
 * @example
 *
 * formatTemplate('{{name}} is {{years}} years old', {
 *   name: 'Joe',
 *   years: 97,
 * })
 * // => 'Joe is 97 years old'
 */
export function formatTemplate(
	str: string | undefined,
	params: Record<string, FormatTemplateValue>,
): string {
	return (str ?? '').replaceAll(/{{(.*?)}}/g, (fullMatch, group1: string) => {
		const key = group1.trim();
		return key && is.defined(params[key]) ? String(params[key]) : '';
	});
}

/**
 * Format a string with mustache placeholders to React nodes.
 *
 * @example
 *
 * formatTemplateToNodes('{{name}} is {{years}} years old', {
 *   name: <strong>Joe</strong>,
 *   years: <FancyNumber num={97} />,
 * })
 * // => [<strong>Joe</strong>, ' is ', <FancyNumber num={97} />, ' years old']
 */
export function formatTemplateToNodes(
	str: string,
	params: Record<string, ReactNode>,
): ReactNode[] {
	return str
		.split(/({{.*?}})/g)
		.map((part, i) => {
			const isTemplatePart = /{{.*?}}/.test(part);
			const key = part.replaceAll(/^{+|}+$/g, '').trim();
			if (key && is.truthyOrZero(params[key])) {
				// Can't use the part string as key since the same placeholder
				// can be included multiple times in the string.
				return createElement(Fragment, { key: i }, params[key]);
			}
			return isTemplatePart ? null : part;
		})
		.filter(is.truthyOrZero);
}

/**
 * Make a URL a bit more pretty to read.
 *
 * @example
 *
 * formatDisplayUrl('https://www.jula.se/')
 * // => 'jula.se'
 */
export function formatDisplayUrl(url: string): string {
	return removeTrailingSlash(
		// Remove leading protocol and/or www.
		url.replace(/^(?:[a-z0-9]{2,}:\/\/)?(?:www\.)?/, ''),
	);
}
